import React from 'react';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

export interface TabDefinition {
	id: string;
	name: string;
	imageFilePath?: string;
}

interface TabStripProps {
	tabs: TabDefinition[];
	selectedTabId: string;
	onTabClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

/*
 * TabStrip Component:
 *
 * Defines a horizontal strip of tabs, one per given tab definition, of which exactly one is selected at a time. The parent owns the
 * selection - it passes the selected tab's ID in and is told, through 'onTabClicked', which tab the customer moved to.
 *
 * Each tab shows its name, optionally preceded by a small swatch image, so that a tab standing for a part of the vehicle can also show
 * what that part currently looks like.
 *
 */
export default function TabStrip(props: TabStripProps) {

	// if a tab is clicked...
	const handleTabClicked = (event: React.MouseEvent<HTMLElement>) => {

		// get the tab ID from the 'data-id' element, and if found, call the callback fn, passing the ID.
		const id = event.currentTarget.dataset.id;
		if (id) {
			props.onTabClicked(id);
		}

		// or log an error.
		else {
			console.error('Missing data-id on element: ', event.currentTarget);
		}
	};

	// return the HTML.
	return (
		<div className="tab-strip" role="tablist">
			{props.tabs.map((tab) => (
				<a
					key={tab.id}
					role="tab"
					aria-selected={tab.id === props.selectedTabId}
					className={`tab-strip-item${tab.id === props.selectedTabId ? ' selected' : ''}`}
					data-id={tab.id}
					onClick={handleTabClicked}
				>
					{tab.imageFilePath &&
						<img draggable={false} className="tab-strip-item-swatch" src={tab.imageFilePath} width="26" height="26" alt={tab.name} />
					}
					<span className="tab-strip-item-name">{tab.name}</span>
				</a>
			))}
		</div>
	);
}
